/**
 * `div.donut` → `donut-chart` (process/08-COMPONENT-CATALOGUE.md), with its own `div.dlegend` →
 * `donut-legend`. A proportion ring, figure in the middle — `Prototype_v2.html`'s "Backup jobs
 * summary" panel. Segment values come straight off `BackupSummary.buckets[].success_rate`/
 * `succeeded`/`partial`/`failed` (rule 1, process/09-SCREEN-SPECS/v-dashboard.md) — the percentage
 * drawn is the served `success_rate`, never recomputed from the segment counts. **The centre
 * figure is a caller-supplied, already-served string** (e.g. `"6/7"` from `success_rate.numerator`/
 * `denominator`) — this component never computes a percentage itself (§24.2 rule 24).
 */
import { cssVar } from '@/styles/tokens'

export interface DonutSegment {
  readonly label: string
  readonly value: number
  readonly colorVar: '--ok' | '--warn' | '--fail'
}

const R = 15.9
const CIRC = 2 * Math.PI * R

export function DonutChart({
  percentDisplay,
  centerSub,
  segments,
  caption,
}: {
  readonly percentDisplay: string
  readonly centerSub: string
  readonly segments: readonly DonutSegment[]
  readonly caption: string
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0)
  const nonZero = segments.filter((s) => s.value > 0)
  const arcs = nonZero.map((s, i) => {
    const offset = nonZero.slice(0, i).reduce((sum, prev) => sum + (total === 0 ? 0 : (prev.value / total) * CIRC), 0)
    const frac = total === 0 ? 0 : s.value / total
    const dash = frac * CIRC
    return <circle key={s.label} cx="21" cy="21" r={R} fill="none" stroke={`var(${s.colorVar})`} strokeWidth="6" strokeDasharray={`${dash} ${CIRC - dash}`} strokeDashoffset={-offset} />
  })

  return (
    <div data-cmp="donut-chart" className="flex flex-col items-center">
      <svg width="150" height="150" viewBox="0 0 42 42">
        <circle cx="21" cy="21" r={R} fill="none" stroke="var(--row)" strokeWidth="6" />
        {arcs}
        <text x="21" y="20.4" textAnchor="middle" style={{ fontSize: '5.4px', fontWeight: cssVar('fw-bold'), fill: 'var(--tx)' }}>
          {percentDisplay}
        </text>
        <text x="21" y="25" textAnchor="middle" style={{ fontSize: '2.6px', fill: 'var(--tx2)' }}>
          {centerSub}
        </text>
      </svg>
      <div data-cmp="donut-legend" className="flex flex-col gap-1 mt-2 text-small">
        {segments.map((s) => (
          <div key={s.label} className="flex items-center gap-2">
            <i className="inline-block w-3 h-3 rounded-pill" style={{ background: `var(${s.colorVar})` }} />
            {s.label} — {s.value}
          </div>
        ))}
      </div>
      <p className="text-small text-tx2 text-center mt-2">{caption}</p>
    </div>
  )
}
